import axios from 'axios';

import { getConfig } from '../../../environmentConfig';
import { bannerState, problemState } from '../../stores';
import { getGuid, getSs, getUsername } from '../../stores/globalState';
import { getMd5 } from '../../stores/problemStore';
import { logHistory, reboot } from '../../utils/gmmUtils';
import { APIObserver, apiQueue } from '../queue';
import { isAxiosError, consoleAxiosError, toBody } from '../utils';

const sessionClosedMessage = 'Session closed -- did you log in somewhere else?';

export const RAISE_HAND_REQUEST_TIMEOUT_MS = 10000;

interface SendRaisedHandAttempt {
  a: number;
  guid: string;
  id: string;
  raise: boolean;
  ss: number;
  user: string;
}

export const sendRaisedHand = async (
  value: boolean,
  // only optional if you're putting hand down
  currentProblemId?: string
): Promise<void> => {
  if (value && !currentProblemId) {
    bannerState().setIsHandRaised(false);
    logHistory('Abandon raise hand: no restore id provided');

    return;
  }

  const capturedMd5 = currentProblemId ? getMd5(currentProblemId) : undefined;
  let staleReason: string | undefined;

  apiQueue.add({
    apiCall: async (apiObserver: APIObserver) => {
      try {
        await sendRaisedHandHelper(value, currentProblemId);
      } finally {
        apiObserver.done();
      }
    },
    isInvalid: () => {
      staleReason = value
        ? getStaleRaiseHandReason(currentProblemId, capturedMd5)
        : undefined;

      return staleReason !== undefined;
    },
    onCancellation: () => {
      if (currentProblemId === bannerState().handRaisedProblemId) {
        bannerState().setIsHandRaised(false);
      }

      logHistory(`Abandon raise hand: ${staleReason}`);
    },
    description: `${value ? 'Raise' : 'Lower'} hand ${currentProblemId ?? ''}`,
  });
};

function getStaleRaiseHandReason(
  currentProblemId: string | undefined,
  capturedMd5: string | undefined
): string | undefined {
  if (!currentProblemId) return 'no restore id provided';

  const selectedId = problemState().selectedID;

  if (selectedId !== currentProblemId) {
    return `selected problem changed from ${currentProblemId} to ${
      selectedId ?? 'none'
    }`;
  }

  const { handRaisedProblemId, isHandRaised } = bannerState();

  if (!isHandRaised || handRaisedProblemId !== currentProblemId) {
    return `hand is no longer raised for problem ${currentProblemId}`;
  }

  if (capturedMd5 === undefined) return undefined;

  const currentMd5 = getMd5(currentProblemId);

  if (currentMd5 !== capturedMd5) {
    return `problem ${currentProblemId} md5 changed from ${capturedMd5} to ${
      currentMd5 ?? 'missing'
    }`;
  }

  return undefined;
}

const sendRaisedHandHelper = async (
  value: boolean,
  currentProblemId?: string
): Promise<void> => {
  try {
    // server has it backwards, sigh
    const realValue = !value;

    const attempt: SendRaisedHandAttempt = {
      a: new Date().getTime(),
      guid: getGuid() || '',
      id: currentProblemId ?? '-1',
      raise: realValue,
      ss: getSs() || 0,
      user: getUsername() || '',
    };

    const url = `${getConfig().JAVA_URL}/Hand`;

    const { data } = await axios.post(url, toBody(attempt), {
      timeout: RAISE_HAND_REQUEST_TIMEOUT_MS,
    });

    if (data.guidFail) {
      reboot(data.guidFail);

      return;
    }

    if (data.sessionClosed) {
      reboot(sessionClosedMessage);

      return;
    }
  } catch (err) {
    if (isAxiosError(err)) {
      return consoleAxiosError(err);
    }

    console.error({ err });

    return;
  }
};
